import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, Clock, Tag } from "lucide-react";
import { blogMetadata, getAllCategories, preloadBlogContent } from "@/data/blog";
import { BlogCollectionSchema } from "@/components/blog/BlogCollectionSchema";

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    document.title = "Google Ads Blog - Uzman İçgörüleri | Dijital Mustafa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "18 yıllık deneyimimle Google Ads stratejileri, ipuçları ve güncellemeler. Bütçe optimizasyonu, sahte tıklama koruması ve daha fazlası.");
    }
  }, []);

  const categories = ["all", ...getAllCategories()];

  const filteredPosts = blogMetadata.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <BlogCollectionSchema posts={filteredPosts} category={selectedCategory} />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#012f23] to-[#059669] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Google Ads Blog
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            18 yıllık deneyimimle Google Ads stratejileri, ipuçları ve güncellemeler
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              placeholder="Blog yazılarında ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#059669] focus:border-transparent"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#059669] focus:border-transparent"
          >
            <option value="all">Tüm Kategoriler</option>
            {categories.filter(c => c !== "all").map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map(post => (
            <Link 
              key={post.id} 
              to={`/blog/${post.slug}`}
              onMouseEnter={() => preloadBlogContent(post.slug, post.category)}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer"
            >
              <div className="overflow-hidden">
                <img 
                  src={post.coverImage} 
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" 
                />
              </div>
              
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-[#059669] text-white text-xs font-bold rounded-full">
                    {post.category}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {post.readTime}
                  </span>
                </div>
                
                <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#059669] transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                  <img 
                    src={post.author.avatar} 
                    alt={post.author.name} 
                    loading="lazy"
                    decoding="async"
                    className="w-10 h-10 rounded-full" 
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{post.author.name}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.publishedAt).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>
                
                <span className="inline-flex items-center gap-2 text-[#059669] font-bold group-hover:gap-3 transition-all">
                  Devamını Oku →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#012f23] to-[#059669] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Blog yazılarıyla öğrendiniz, şimdi harekete geçme zamanı!
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            15 dakikalık ücretsiz görüşmede kampanyalarınızı analiz edip size özel strateji sunuyorum.
          </p>
          <a
            href="https://wa.me/905326369115?text=Merhaba%20Mustafa%20Bey%2C%20Google%20Ads%20kampanyalar%C4%B1m%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:bg-[#20BA5A] transition-colors shadow-lg"
          >
            WhatsApp ile İletişime Geç
          </a>
        </div>
      </section>
    </div>
    </>
  );
};

export default Blog;
